import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, CheckSquare, ChevronDown, ChevronUp, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TaskManagement from "./TaskManagement";

export default function CalendarView() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailyRecords, setDailyRecords] = useState([]);
  const [mySites, setMySites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(""); // Selected site for filtering
  const [allTasks, setAllTasks] = useState([]); // All tasks from all sites
  const [expandedSites, setExpandedSites] = useState({}); // Track which sites are expanded
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [todoText, setTodoText] = useState("");
  const [savingTodo, setSavingTodo] = useState(false);
  const [tasksPerDate, setTasksPerDate] = useState({}); // Track tasks count per date
  const [nonWorkingDays, setNonWorkingDays] = useState(new Set()); // Track non-working weekdays (public holidays)
  const [workingWeekendOverrides, setWorkingWeekendOverrides] = useState(new Set()); // Track weekends marked as "working" (visual only)
  
  // Task management states
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showAddProjectDialog, setShowAddProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [siteManagers, setSiteManagers] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("incomplete");
  const [priorityFilter, setPriorityFilter] = useState("all");

  useEffect(() => {
    loadMySites();
  }, []);

  useEffect(() => {
    // Set first site as default when sites are loaded
    if (mySites.length > 0 && !selectedSite) {
      setSelectedSite(mySites[0].id);
    }
  }, [mySites]);

  useEffect(() => {
    if (mySites.length > 0 && selectedSite) {
      loadMonthRecords();
      loadTasksForMonth();
      loadAllTasks();
      loadNonWorkingDays();
      loadWorkingWeekendOverrides();
    }
  }, [currentDate, mySites, selectedSite]);

  const loadNonWorkingDays = async () => {
    if (!selectedSite) return;
    
    try {
      const token = localStorage.getItem("token");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // Get first and last day of month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      const response = await fetch(
        `${API}/non-working-days?site_id=${selectedSite}&start_date=${startDateStr}&end_date=${endDateStr}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Store only weekdays as Set of date strings
        setNonWorkingDays(new Set(data.map(d => d.date)));
      }
    } catch (error) {
      console.error("Error loading non-working days:", error);
    }
  };

  const loadWorkingWeekendOverrides = async () => {
    if (!selectedSite) return;
    
    try {
      const token = localStorage.getItem("token");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      const response = await fetch(
        `${API}/working-weekend-overrides?site_id=${selectedSite}&start_date=${startDateStr}&end_date=${endDateStr}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setWorkingWeekendOverrides(new Set(data.map(d => d.date)));
      }
    } catch (error) {
      console.error("Error loading working weekend overrides:", error);
    }
  };

  const loadMySites = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Get all sites if admin, or only assigned sites for other users
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const allSites = await response.json();
        
        // Filter sites based on user role
        if (user.role === "admin") {
          setMySites(allSites);
        } else {
          // For non-admin users (site_manager, view_only, supervisor), 
          // filter by assigned_sites from user profile
          const filteredSites = allSites.filter(site => 
            user.assigned_sites && user.assigned_sites.includes(site.id)
          );
          setMySites(filteredSites);
        }
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    }
  };

  const loadMonthRecords = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // Get first and last day of month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      // Use timezone-safe date formatting
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      // Fetch records for selected site only
      const response = await fetch(
        `${API}/daily-records?site_id=${selectedSite}&start_date=${startDateStr}&end_date=${endDateStr}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const records = await response.json();
        const site = mySites.find(s => s.id === selectedSite);
        setDailyRecords(records.map(r => ({ ...r, site_name: site?.name })));
      }
    } catch (error) {
      console.error("Error loading records:", error);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const loadTasksForMonth = async () => {
    try {
      const token = localStorage.getItem("token");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // Get first and last day of month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      // Use timezone-safe date formatting
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      // Fetch tasks for all my sites
      const tasksByDate = {};
      for (const site of mySites) {
        const response = await fetch(
          `${API}/tasks?site_id=${site.id}`,
          { headers: { "Authorization": `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const tasks = await response.json();
          // Group tasks by date
          tasks.forEach(task => {
            const date = task.scheduled_date;
            if (date >= startDateStr && date <= endDateStr) {
              if (!tasksByDate[date]) {
                tasksByDate[date] = { total: 0, incomplete: 0 };
              }
              tasksByDate[date].total++;
              if (task.status === 'incomplete' || task.status === 'in_progress') {
                tasksByDate[date].incomplete++;
              }
            }
          });
        }
      }
      
      setTasksPerDate(tasksByDate);
    } catch (error) {
      console.error("Error loading tasks for month:", error);
    }
  };

  const loadAllTasks = async () => {
    try {
      const token = localStorage.getItem("token");
      const tasksWithSites = [];
      
      // Fetch tasks for all my sites
      for (const site of mySites) {
        const response = await fetch(
          `${API}/tasks?site_id=${site.id}`,
          { headers: { "Authorization": `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const tasks = await response.json();
          // Add site info to each task
          tasks.forEach(task => {
            tasksWithSites.push({
              ...task,
              site_name: site.name,
              site_id: site.id
            });
          });
        }
      }
      
      setAllTasks(tasksWithSites);
    } catch (error) {
      console.error("Error loading all tasks:", error);
    }
  };

  const handleToggleTaskStatus = async (task) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = task.status === "complete" ? "incomplete" : "complete";
      
      const response = await fetch(`${API}/tasks/${task.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Update local state
        setAllTasks(prev => prev.map(t => 
          t.id === task.id ? { ...t, status: newStatus } : t
        ));
        loadTasksForMonth(); // Refresh calendar indicators
        toast.success(newStatus === "complete" ? "Task marked as complete!" : "Task marked as incomplete");
      } else {
        toast.error("Failed to update task");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const toggleSiteExpansion = (siteId) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const allTasksData = [];
      
      for (const site of mySites) {
        const response = await fetch(`${API}/tasks?site_id=${site.id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (response.ok) {
          const tasks = await response.json();
          allTasksData.push(...tasks);
        }
      }
      
      // Extract unique project names
      const uniqueProjects = [...new Set(
        allTasksData
          .map(task => task.project)
          .filter(project => project && project.trim() !== "")
      )].sort();
      setProjects(uniqueProjects);
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const loadSiteManagers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/site-managers`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const managers = await response.json();
        setSiteManagers(managers);
      }
    } catch (error) {
      console.error("Error loading site managers:", error);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowEditDialog(true);
    // Load projects and managers when opening edit dialog
    if (projects.length === 0) loadProjects();
    if (siteManagers.length === 0) loadSiteManagers();
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setAllTasks(prev => prev.map(task => 
          task.id === taskId ? { ...updatedTask, siteName: task.siteName } : task
        ));
        loadTasksForMonth();
        if (updates.project !== undefined) loadProjects();
        toast.success("Task updated successfully!");
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to update task");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        setAllTasks(prev => prev.filter(task => task.id !== taskId));
        loadTasksForMonth();
        toast.success("Task deleted successfully!");
      } else {
        toast.error("Failed to delete task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const getFilteredTasks = () => {
    let filtered = [...allTasks];
    
    // Apply status filter
    if (statusFilter === "incomplete") {
      // Show both incomplete and in_progress when "incomplete" is selected
      filtered = filtered.filter(task => task.status === "incomplete" || task.status === "in_progress");
    } else if (statusFilter !== "all") {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    
    // Apply priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }
    
    return filtered;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = firstDay.getDay();
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add all days in month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getRecordsForDate = (date) => {
    if (!date) return [];
    // Use timezone-safe date formatting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return dailyRecords.filter(record => record.date === dateStr);
  };

  const getPersonnelCountsForDate = (date) => {
    const records = getRecordsForDate(date);
    let dayLabourCount = 0;
    let contractorCount = 0;
    
    records.forEach(record => {
      // Calculate day labour count
      if (record.day_labour) {
        dayLabourCount += record.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0);
      }
      // Calculate contractor count
      if (record.subcontractors) {
        contractorCount += record.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0);
      }
    });
    
    return { dayLabourCount, contractorCount, total: dayLabourCount + contractorCount };
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    // Format the date as YYYY-MM-DD (timezone-safe)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Navigate to dashboard with the selected date
    navigate('/dashboard', { state: { selectedDate: dateStr } });
  };

  const handleUpdateTodo = async () => {
    if (!selectedRecord) {
      toast.error("No record selected");
      return;
    }

    setSavingTodo(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/daily-records/${selectedRecord.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...selectedRecord,
          todo_list: todoText
        })
      });

      if (response.ok) {
        toast.success("To-do list updated successfully!");
        // Update local state
        setDailyRecords(prev => prev.map(r => 
          r.id === selectedRecord.id ? { ...r, todo_list: todoText } : r
        ));
        setSelectedRecord(prev => ({ ...prev, todo_list: todoText }));
      } else {
        toast.error("Failed to update to-do list");
      }
    } catch (error) {
      console.error("Error updating todo:", error);
      toast.error("Failed to update to-do list");
    } finally {
      setSavingTodo(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const toggleNonWorkingDay = async (dateStr, isWeekend, e) => {
    e.stopPropagation(); // Prevent day click event
    
    if (!selectedSite) {
      toast.error("Please select a site first");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      
      if (isWeekend) {
        // For weekends: toggle between default grey (not in override list) and clear (in override list)
        // But always excluded from calculations
        const isOverridden = workingWeekendOverrides.has(dateStr);
        
        if (isOverridden) {
          // Remove override - return to default grey
          const response = await fetch(`${API}/working-weekend-overrides/${selectedSite}/${dateStr}`, {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${token}` }
          });
          
          if (response.ok) {
            setWorkingWeekendOverrides(prev => {
              const newSet = new Set(prev);
              newSet.delete(dateStr);
              return newSet;
            });
            toast.success("Weekend returned to non-working status");
          }
        } else {
          // Add override - mark as "working" visually (but still excluded from calculations)
          const response = await fetch(`${API}/working-weekend-overrides`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              site_id: selectedSite,
              date: dateStr
            })
          });
          
          if (response.ok) {
            setWorkingWeekendOverrides(prev => new Set([...prev, dateStr]));
            toast.success("Weekend marked as bonus catchup day");
          }
        }
      } else {
        // For weekdays: toggle non-working day (e.g., public holiday)
        const isNonWorking = nonWorkingDays.has(dateStr);
        
        if (isNonWorking) {
          // Remove non-working day - make it a working day
          const response = await fetch(`${API}/non-working-days/${selectedSite}/${dateStr}`, {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${token}` }
          });
          
          if (response.ok) {
            setNonWorkingDays(prev => {
              const newSet = new Set(prev);
              newSet.delete(dateStr);
              return newSet;
            });
            toast.success("Day marked as working day");
          }
        } else {
          // Add non-working day (public holiday)
          const response = await fetch(`${API}/non-working-days`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              site_id: selectedSite,
              date: dateStr
            })
          });
          
          if (response.ok) {
            setNonWorkingDays(prev => new Set([...prev, dateStr]));
            toast.success("Day marked as non-working day (public holiday)");
          }
        }
      }
    } catch (error) {
      console.error("Error toggling day status:", error);
      toast.error("Failed to update day status");
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading && dailyRecords.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <CalendarIcon className="w-8 h-8" />
              Calendar View
            </h1>
            <p className="text-slate-600 mt-1">
              Daily records for {user.role === "admin" ? "all sites" : "your assigned sites"}
            </p>
          </div>
          
          {/* Site Selector */}
          {mySites.length > 0 && (
            <div className="w-full md:w-64">
              <Label htmlFor="site-select" className="text-sm font-medium text-slate-700 mb-1 block">
                Select Site
              </Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger id="site-select" className="w-full">
                  <SelectValue placeholder="Choose a site" />
                </SelectTrigger>
                <SelectContent>
                  {mySites.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="outline" onClick={goToPreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <Button variant="outline" onClick={goToToday} size="sm">
              Today
            </Button>
          </div>
          <Button variant="outline" onClick={goToNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Calendar Grid */}
        <div className="max-w-4xl">
          <Card>
            <CardContent className="p-2 md:p-4">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayNames.map(day => (
                  <div key={day} className="text-center font-semibold text-xs text-slate-600 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} className="h-14 md:h-16" />;
                  }

                  const records = getRecordsForDate(day);
                  const personnelCounts = getPersonnelCountsForDate(day);
                  // Use timezone-safe date formatting for task lookup
                  const year = day.getFullYear();
                  const month = String(day.getMonth() + 1).padStart(2, '0');
                  const dayNum = String(day.getDate()).padStart(2, '0');
                  const dateStr = `${year}-${month}-${dayNum}`;
                  const taskInfo = tasksPerDate[dateStr];
                  const isToday = day.toDateString() === new Date().toDateString();
                  const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                  const hasRecords = records.length > 0;
                  const hasTodos = records.some(r => r.todo_list && r.todo_list.trim());
                  const hasTasks = taskInfo && taskInfo.total > 0;
                  const hasIncompleteTasks = taskInfo && taskInfo.incomplete > 0;
                  
                  // Check if day is lost day (rain/weather)
                  const isLostDay = records.some(r => r.lost_day === true);
                  
                  // Check if day is weekend (Saturday=6, Sunday=0)
                  const dayOfWeek = day.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  // Weekend override: if weekend is in workingWeekendOverrides, show as clear (not grey)
                  const isWeekendOverride = isWeekend && workingWeekendOverrides.has(dateStr);
                  
                  // Weekday non-working: if weekday is in nonWorkingDays, show as grey
                  const isWeekdayNonWorking = !isWeekend && nonWorkingDays.has(dateStr);
                  
                  // For display: grey shading if weekend (unless overridden) OR weekday marked as non-working
                  const showGreyShading = (isWeekend && !isWeekendOverride) || isWeekdayNonWorking;
                  
                  // For checkbox: checked if showing grey
                  const isChecked = showGreyShading;

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => handleDateClick(day)}
                      className={`
                        h-14 md:h-16 p-1 rounded-md border-2 transition-all text-xs cursor-pointer hover:shadow-md relative
                        ${isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}
                        ${isToday ? "bg-amber-50" : ""}
                        ${isLostDay ? "bg-blue-300" : ""}
                        ${showGreyShading && !isLostDay ? "bg-slate-300 opacity-70" : ""}
                      `}
                    >
                      {/* Non-working day toggle square - no checkbox tick */}
                      {user.role !== "view_only" && (
                        <div 
                          className={`absolute top-0.5 right-0.5 z-10 w-3 h-3 border border-slate-400 cursor-pointer rounded-sm ${
                            isChecked ? "bg-slate-600" : "bg-white"
                          }`}
                          onClick={(e) => toggleNonWorkingDay(dateStr, isWeekend, e)}
                          title={
                            isWeekend 
                              ? (isWeekendOverride ? "Mark as non-working (always excluded from calculations)" : "Mark as bonus catchup day (still excluded from calculations)")
                              : (isWeekdayNonWorking ? "Mark as working day" : "Mark as public holiday (excluded from calculations)")
                          }
                        />
                      )}
                      
                      <div className="flex flex-col items-center justify-start h-full">
                        <span className={`font-semibold text-sm ${isToday ? "text-amber-700" : ""}`}>
                          {day.getDate()}
                        </span>
                        <div className="flex flex-col items-center gap-0.5 mt-0.5 w-full">
                          {personnelCounts.total > 0 && (
                            <div className="flex flex-col items-center leading-tight text-[0.65rem] font-medium">
                              {personnelCounts.dayLabourCount > 0 && (
                                <div className="text-blue-700" title="Day Labour">
                                  DL:{personnelCounts.dayLabourCount}
                                </div>
                              )}
                              {personnelCounts.contractorCount > 0 && (
                                <div className="text-green-700" title="Contractors">
                                  C:{personnelCounts.contractorCount}
                                </div>
                              )}
                            </div>
                          )}
                          {hasTasks && hasIncompleteTasks && (
                            <div className="w-2 h-2 rounded-full bg-orange-500" title={`${taskInfo.incomplete} incomplete tasks`}></div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Tasks Panel - Hidden for view_only users */}
        {user.role !== "view_only" && (
          <div>
            <Card>
              <CardHeader className="bg-slate-700 text-white">
                <CardTitle className="text-lg">
                  <div className="flex items-center justify-between">
                    <span>All Tasks</span>
                    <Badge variant="secondary" className="bg-white text-slate-700">
                      {user.username}
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
            <CardContent className="p-4 max-h-[700px] overflow-y-auto">
              {/* Filters */}
              <div className="mb-4 flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs font-semibold mb-1 block">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="incomplete">Incomplete</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-semibold mb-1 block">Priority</Label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {mySites.length === 0 ? (
                <p className="text-slate-500 text-sm">No sites assigned</p>
              ) : getFilteredTasks().length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">No tasks found matching filters</p>
              ) : (
                <div className="space-y-4">
                  {mySites.map(site => {
                    const filteredTasks = getFilteredTasks();
                    const siteTasks = filteredTasks.filter(task => task.site_id === site.id);
                    
                    if (siteTasks.length === 0) return null;
                    
                    const isExpanded = expandedSites[site.id] !== false; // Default to expanded
                    
                    return (
                      <div key={site.id} className="border-2 border-blue-600 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSiteExpansion(site.id)}
                          className="w-full bg-blue-600 text-white px-3 py-2 flex items-center justify-between hover:bg-blue-700 transition-colors"
                        >
                          <div className="text-left">
                            <h3 className="font-bold text-sm">{site.name}</h3>
                            <p className="text-xs opacity-90">{siteTasks.length} task{siteTasks.length !== 1 ? 's' : ''}</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="bg-white">
                            {/* Desktop Table View */}
                            <div className="hidden md:block">
                              <table className="w-full text-xs">
                                <tbody>
                                  {siteTasks.map((task, idx) => {
                                    const isOverdue = task.status !== "complete" && new Date(task.scheduled_date) < new Date();
                                    
                                    return (
                                      <tr
                                        key={task.id}
                                        className={`border-b last:border-b-0 ${
                                          task.status === "complete" 
                                            ? "bg-green-50" 
                                            : isOverdue
                                            ? "bg-red-50"
                                            : idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                                        }`}
                                      >
                                        <td className="px-2 py-2 w-6">
                                          <input
                                            type="checkbox"
                                            checked={task.status === "complete"}
                                            onChange={() => handleToggleTaskStatus(task)}
                                            className="w-3 h-3 rounded"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <div className={`font-medium ${task.status === "complete" ? "line-through text-gray-500" : ""}`}>
                                            {task.title}
                                          </div>
                                          {task.description && (
                                            <div className="text-gray-600 text-xs mt-1">
                                              {task.description}
                                            </div>
                                          )}
                                          {task.project && (
                                            <div className="text-blue-600 text-xs mt-1 font-medium">
                                              📁 {task.project}
                                            </div>
                                          )}
                                          <div className="flex items-center gap-2 mt-1">
                                            <Badge className={`text-xs px-1 py-0 ${
                                              task.priority === "high" ? "bg-red-100 text-red-800" :
                                              task.priority === "medium" ? "bg-yellow-100 text-yellow-800" :
                                              "bg-gray-100 text-gray-800"
                                            }`}>
                                              {task.priority}
                                            </Badge>
                                            <span className="text-xs text-gray-600">
                                              {task.scheduled_date}
                                            </span>
                                            {isOverdue && (
                                              <span className="text-xs text-red-600 font-semibold">Overdue!</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-2 py-2 w-20">
                                          <div className="flex gap-1">
                                            <button
                                              onClick={() => handleEditTask(task)}
                                              className="p-1 hover:bg-blue-100 rounded transition-colors"
                                              title="Edit task"
                                            >
                                              <Edit className="w-3 h-3 text-blue-600" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteTask(task.id)}
                                              className="p-1 hover:bg-red-100 rounded transition-colors"
                                              title="Delete task"
                                            >
                                              <Trash2 className="w-3 h-3 text-red-600" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Mobile Card View */}
                            <div className="md:hidden space-y-2 p-2">
                              {siteTasks.map((task) => {
                                const isOverdue = task.status !== "complete" && new Date(task.scheduled_date) < new Date();
                                
                                return (
                                  <div
                                    key={task.id}
                                    className={`border rounded-lg p-3 ${
                                      task.status === "complete" 
                                        ? "bg-green-50 border-green-200" 
                                        : isOverdue
                                        ? "bg-red-50 border-red-200"
                                        : "bg-white border-slate-200"
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <input
                                        type="checkbox"
                                        checked={task.status === "complete"}
                                        onChange={() => handleToggleTaskStatus(task)}
                                        className="w-5 h-5 rounded mt-0.5"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className={`font-medium text-sm ${task.status === "complete" ? "line-through text-gray-500" : ""}`}>
                                          {task.title}
                                        </div>
                                        {task.description && (
                                          <div className="text-gray-600 text-xs mt-1">
                                            {task.description}
                                          </div>
                                        )}
                                        {task.project && (
                                          <div className="text-blue-600 text-xs mt-1 font-medium">
                                            📁 {task.project}
                                          </div>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                          <Badge className={`text-xs px-2 py-0.5 ${
                                            task.priority === "high" ? "bg-red-100 text-red-800" :
                                            task.priority === "medium" ? "bg-yellow-100 text-yellow-800" :
                                            "bg-gray-100 text-gray-800"
                                          }`}>
                                            {task.priority}
                                          </Badge>
                                          <span className="text-xs text-gray-600">
                                            {task.scheduled_date}
                                          </span>
                                          {isOverdue && (
                                            <span className="text-xs text-red-600 font-semibold">Overdue!</span>
                                          )}
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                          <button
                                            onClick={() => handleEditTask(task)}
                                            className="flex-1 px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                          >
                                            <Edit className="w-3 h-3 inline mr-1" />
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => handleDeleteTask(task.id)}
                                            className="flex-1 px-3 py-2 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                          >
                                            <Trash2 className="w-3 h-3 inline mr-1" />
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Edit Task Dialog */}
      {editingTask && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-task-title">Task Title *</Label>
                <Input
                  id="edit-task-title"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-task-description">Description</Label>
                <Textarea
                  id="edit-task-description"
                  value={editingTask.description || ""}
                  onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-task-project">Project</Label>
                <Select 
                  value={editingTask.project || "__none__"} 
                  onValueChange={(value) => {
                    if (value === "__add_new__") {
                      setShowAddProjectDialog(true);
                    } else if (value === "__none__") {
                      setEditingTask(prev => ({ ...prev, project: "" }));
                    } else {
                      setEditingTask(prev => ({ ...prev, project: value }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No Project</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project} value={project}>
                        {project}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__">+ Add New Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-task-date">Scheduled Date</Label>
                  <Input
                    id="edit-task-date"
                    type="date"
                    value={editingTask.scheduled_date}
                    onChange={(e) => setEditingTask(prev => ({ ...prev, scheduled_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-priority">Priority</Label>
                  <Select value={editingTask.priority} onValueChange={(value) => setEditingTask(prev => ({ ...prev, priority: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-task-status">Status</Label>
                <Select value={editingTask.status} onValueChange={(value) => setEditingTask(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incomplete">Incomplete</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assign To (Optional)</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {siteManagers.map((manager) => (
                    <div key={manager.id} className="flex items-center space-x-2">
                      <Checkbox
                        checked={editingTask.assigned_to?.includes(manager.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEditingTask(prev => ({
                              ...prev,
                              assigned_to: [...(prev.assigned_to || []), manager.id]
                            }));
                          } else {
                            setEditingTask(prev => ({
                              ...prev,
                              assigned_to: (prev.assigned_to || []).filter(id => id !== manager.id)
                            }));
                          }
                        }}
                      />
                      <Label className="text-sm">{manager.username}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingTask(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  await handleUpdateTask(editingTask.id, {
                    title: editingTask.title,
                    description: editingTask.description,
                    scheduled_date: editingTask.scheduled_date,
                    project: editingTask.project,
                    priority: editingTask.priority,
                    status: editingTask.status,
                    assigned_to: editingTask.assigned_to
                  });
                  setShowEditDialog(false);
                  setEditingTask(null);
                }}
                disabled={!editingTask.title.trim()}
              >
                Update Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add New Project Dialog */}
      <Dialog open={showAddProjectDialog} onOpenChange={setShowAddProjectDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-project-name-calendar">Project Name *</Label>
              <Input
                id="new-project-name-calendar"
                placeholder="Enter project name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddProjectDialog(false);
                setNewProjectName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (newProjectName.trim()) {
                  setProjects(prev => [...new Set([...prev, newProjectName.trim()])].sort());
                  if (editingTask) {
                    setEditingTask(prev => ({ ...prev, project: newProjectName.trim() }));
                  }
                  setShowAddProjectDialog(false);
                  setNewProjectName("");
                  toast.success("Project added successfully!");
                }
              }}
              disabled={!newProjectName.trim()}
            >
              Add Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
